import React from 'react';
import {
    DocumentTextIcon,
    ChatBubbleLeftRightIcon,
    UserGroupIcon,
    CalendarIcon,
    CheckCircleIcon,
    ExclamationCircleIcon
} from '@heroicons/react/24/outline';

export interface ActivityItem {
    id: string;
    type: 'document' | 'message' | 'client' | 'meeting' | 'task' | 'alert';
    title: string;
    description?: string;
    timestamp: Date;
    user?: string;
    status?: 'success' | 'warning' | 'error' | 'info';
}

interface ActivityFeedProps {
    activities: ActivityItem[];
    maxItems?: number;
    showViewAll?: boolean;
    onViewAll?: () => void;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
    activities,
    maxItems = 10,
    showViewAll = true,
    onViewAll
}) => {
    const getIcon = (type: ActivityItem['type']) => {
        switch (type) {
            case 'document':
                return DocumentTextIcon;
            case 'message':
                return ChatBubbleLeftRightIcon;
            case 'client':
                return UserGroupIcon;
            case 'meeting':
                return CalendarIcon;
            case 'task':
                return CheckCircleIcon;
            case 'alert':
                return ExclamationCircleIcon;
            default:
                return DocumentTextIcon;
        }
    };

    const getIconColor = (type: ActivityItem['type'], status?: ActivityItem['status']) => {
        if (status === 'error') return 'text-red-500 bg-red-100';
        if (status === 'warning') return 'text-amber-500 bg-amber-100';

        switch (type) {
            case 'document':
                return 'text-purple-500 bg-purple-100';
            case 'message':
                return 'text-blue-500 bg-blue-100';
            case 'client':
                return 'text-green-500 bg-green-100';
            case 'meeting':
                return 'text-indigo-500 bg-indigo-100';
            case 'task':
                return 'text-emerald-500 bg-emerald-100';
            case 'alert':
                return 'text-red-500 bg-red-100';
            default:
                return 'text-gray-500 bg-gray-100';
        }
    };

    const formatTime = (date: Date) => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;

        return date.toLocaleDateString();
    };

    const displayActivities = activities.slice(0, maxItems);

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            </div>

            <div className="divide-y divide-gray-200">
                {displayActivities.length > 0 ? (
                    displayActivities.map((activity) => {
                        const Icon = getIcon(activity.type);
                        const iconColorClass = getIconColor(activity.type, activity.status);

                        return (
                            <div key={activity.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                                <div className="flex items-start space-x-3">
                                    <div className={`p-2 rounded-lg ${iconColorClass}`}>
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900">
                                            {activity.title}
                                        </p>
                                        {activity.description && (
                                            <p className="text-sm text-gray-600 mt-1">
                                                {activity.description}
                                            </p>
                                        )}
                                        <div className="flex items-center mt-2 text-xs text-gray-500">
                                            <span>{formatTime(activity.timestamp)}</span>
                                            {activity.user && (
                                                <>
                                                    <span className="mx-1">•</span>
                                                    <span>{activity.user}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="px-6 py-8 text-center">
                        <p className="text-gray-500 text-sm">No recent activity</p>
                    </div>
                )}
            </div>

            {showViewAll && activities.length > maxItems && (
                <div className="px-6 py-3 border-t border-gray-200">
                    <button
                        onClick={onViewAll}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                        View all activity →
                    </button>
                </div>
            )}
        </div>
    );
};
